import * as React from "react";
import { ChevronDown, FileText } from "lucide-react";
import { useToggle } from "react-use";
import { cn } from "@/lib/utils";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { reformatCitations } from "@/modules/chat/chat.utils";
import type { FamilySource } from "@/modules/snapshots/outcome-details/types";
import { isEvidenceForPrompt } from "@/modules/snapshots/grid/sidebar/sourceDocument";
import { EventDateAlert } from "@/modules/snapshots/outcome-details/components/EventDateAlert";
import { RagStatusBadge } from "./ragStatusBadge";

interface Props {
  sources: FamilySource[];
  businessReportPromptId: string;
}

interface SourceResponseProps {
  source: FamilySource;
  businessReportPromptId: string;
}

const SourceResponse: React.FC<SourceResponseProps> = ({ source: { outcome, evidences }, businessReportPromptId }) => {
  const cell = outcome.result.find(row => !row.is_note && row.business_report_prompt_id === businessReportPromptId);
  // Each source cites its own document, so its citations open that document in the PDF panel.
  const promptEvidences = React.useMemo(
    () => evidences.filter(evidence => isEvidenceForPrompt(evidence.business_report_prompt_id, businessReportPromptId)),
    [evidences, businessReportPromptId],
  );
  const documentName = outcome.fs_entry?.document_name ?? outcome.name;
  const relevancy = cell?.data_type === "relevancy" ? (cell.relevancy ?? null) : null;
  const hasValue = !!cell && (!!cell.result || !!cell.date || !!cell.rating || relevancy != null);

  return (
    <article className="rounded-md border border-[#e7dfd5] bg-white px-3 py-2">
      <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
        <FileText className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
        <span className="truncate" title={documentName}>
          {documentName}
        </span>
      </h4>
      <div className="mt-1 space-y-2 text-sm leading-relaxed">
        {cell?.rating && <RagStatusBadge rating={cell.rating} />}
        {relevancy != null && <p>{relevancy ? "True" : "False"}</p>}
        {cell?.result && (
          <MarkdownRenderer
            content={reformatCitations(cell.result)}
            evidences={promptEvidences}
            processCitations={true}
          />
        )}
        {cell?.date && <EventDateAlert date={cell.date} />}
        {!hasValue && <span className="text-muted-foreground">No response</span>}
      </div>
    </article>
  );
};

/** The responses of a contract summary's source documents to one prompt, collapsed by default. */
export const FamilySourceResponses: React.FC<Props> = ({ sources, businessReportPromptId }) => {
  const [isExpanded, toggleExpanded] = useToggle(false);
  const regionId = React.useId();

  if (sources.length === 0) {
    return null;
  }

  return (
    <div className="mt-3">
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={regionId}
        onClick={toggleExpanded}
        className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
      >
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", isExpanded && "rotate-180")} aria-hidden />
        {isExpanded ? "Hide" : "Show"} responses from {sources.length} source document{sources.length === 1 ? "" : "s"}
      </button>
      {isExpanded && (
        <div id={regionId} role="region" aria-label="Source document responses" className="mt-2 space-y-2">
          {sources.map(source => (
            <SourceResponse key={source.outcome.id} source={source} businessReportPromptId={businessReportPromptId} />
          ))}
        </div>
      )}
    </div>
  );
};
